import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Modal,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip
} from "@material-ui/core";
import {
  FileText,
  Search,
  BookOpen,
  Video,
  BarChart3,
  MessageCircle,
  Zap,
  KanbanSquare,
  Users,
  CalendarPlus,
  Tag,
  Send,
  List,
  Sliders,
  MessagesSquare,
  ListTodo,
  SmartphoneNfc,
  FolderSymlink,
  Settings,
  GitMerge,
  UserPlus,
  Landmark,
  FileJson2,
  Bot,
  Workflow,
  Archive,
  HelpCircle,
  X,
  Plus,
  Edit,
  Trash2,
  Play,
  HardDrive
} from "lucide-react";
import Markdown from "markdown-to-jsx";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TermsOfUseModal from "../../components/TermsOfUseModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { manuals } from "../../components/ManualButton/manuals";

export const parseVideoUrl = (urlOrId) => {
  if (!urlOrId) return { type: "unknown", embedUrl: "", thumbnail: null };

  const clean = urlOrId.trim();

  // 1. YouTube
  const ytMatch = clean.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/
  );
  if (ytMatch && ytMatch[1]) {
    const videoId = ytMatch[1];
    return {
      type: "youtube",
      videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
      thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    };
  } else if (/^[\w-]{11}$/.test(clean)) {
    return {
      type: "youtube",
      videoId: clean,
      embedUrl: `https://www.youtube.com/embed/${clean}?autoplay=1`,
      thumbnail: `https://img.youtube.com/vi/${clean}/mqdefault.jpg`
    };
  }

  // 2. Google Drive
  const driveMatch =
    clean.match(/drive\.google\.com\/file\/d\/([\w-]+)/) ||
    clean.match(/drive\.google\.com\/open\?id=([\w-]+)/);
  if (driveMatch && driveMatch[1]) {
    const fileId = driveMatch[1];
    return {
      type: "gdrive",
      fileId,
      embedUrl: `https://drive.google.com/file/d/${fileId}/preview`,
      thumbnail: null
    };
  }

  // 3. Vimeo
  const vimeoMatch = clean.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch && vimeoMatch[1]) {
    const videoId = vimeoMatch[1];
    return {
      type: "vimeo",
      videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1`,
      thumbnail: null
    };
  }

  // 4. Direct MP4 / WebM / OGG / MOV
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(clean)) {
    return {
      type: "direct",
      embedUrl: clean,
      thumbnail: null
    };
  }

  // 5. Generic embed fallback
  return {
    type: "generic",
    embedUrl: clean,
    thumbnail: null
  };
};

const routeIcons = {
  "/": BarChart3,
  "/tickets": MessageCircle,
  "/quick-messages": Zap,
  "/templates": FileText,
  "/Kanban": KanbanSquare,
  "/contacts": Users,
  "/schedules": CalendarPlus,
  "/birthday-settings": CalendarPlus,
  "/tags": Tag,
  "/campaigns": Send,
  "/contact-lists": List,
  "/campaigns-config": Sliders,
  "/campaign-report": BarChart3,
  "/chats": MessagesSquare,
  "/todolist": ListTodo,
  "/connections": SmartphoneNfc,
  "/files": FolderSymlink,
  "/settings": Settings,
  "/queues": GitMerge,
  "/users": UserPlus,
  "/financeiro": Landmark,
  "/messages-api": FileJson2,
  "/prompts": Bot,
  "/queue-integration": Workflow,
  "/announcements": Send,
  "/backup": Archive,
  "/reports": BarChart3,
  "/moments": BarChart3,
};

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  tabsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  tabLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
  },
  addVideoButton: {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    gap: theme.spacing(1),
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
    width: "100%",
    maxWidth: 600,
  },
  searchInput: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
    },
  },
  mainPaperContainer: {
    overflowY: "auto",
    maxHeight: "calc(100vh - 280px)",
    paddingRight: theme.spacing(1),
  },
  mainPaper: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  manualCard: {
    position: "relative",
    width: "100%",
    minHeight: "220px",
    padding: theme.spacing(2.5),
    boxShadow: theme.shadows[2],
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
      borderColor: theme.palette.primary.main,
      "& $cardIcon": {
        transform: "scale(1.1)",
        color: theme.palette.primary.main,
      },
      "& $readMoreText": {
        color: theme.palette.primary.main,
      },
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  cardIconBox: {
    width: 48,
    height: 48,
    borderRadius: "10px",
    backgroundColor: theme.palette.action.hover,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardIcon: {
    transition: "transform 0.3s ease, color 0.3s ease",
    color: theme.palette.text.secondary,
  },
  routeBadge: {
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: "6px",
    backgroundColor: theme.palette.action.selected,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "1.05rem",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  cardDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    lineHeight: 1.5,
    flexGrow: 1,
    marginBottom: theme.spacing(2),
  },
  readMoreText: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    transition: "color 0.3s ease",
  },
  helpPaper: {
    position: "relative",
    width: "100%",
    minHeight: "340px",
    padding: theme.spacing(2),
    boxShadow: theme.shadows[3],
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
  },
  paperHover: {
    transition: "transform 0.3s, box-shadow 0.3s",
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: theme.shadows[6],
      borderColor: theme.palette.primary.main,
    },
  },
  mediaBox: {
    position: "relative",
    width: "100%",
    height: "calc(100% - 70px)",
    borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 0`,
    overflow: "hidden",
  },
  videoThumbnail: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  genericThumbnail: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    color: "#fff",
    gap: theme.spacing(1),
  },
  cardActionOverlay: {
    position: "absolute",
    top: 8,
    right: 8,
    display: "flex",
    gap: 4,
    zIndex: 2,
  },
  videoTitle: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  videoDescription: {
    maxHeight: "60px",
    overflow: "hidden",
    color: theme.palette.text.secondary,
  },
  videoModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  videoModalContent: {
    outline: "none",
    width: "90%",
    maxWidth: 1024,
    aspectRatio: "16/9",
    position: "relative",
    backgroundColor: "black",
    borderRadius: theme.spacing(1),
    overflow: "hidden",
  },
  termsThumbnail: {
    width: "100%",
    height: "calc(100% - 70px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 0`,
  },
  dialogTitle: {
    background: "linear-gradient(to right, #182229, #202d37)",
    color: "#ffffff",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
  },
  dialogTitleText: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.2rem",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  dialogContent: {
    padding: "24px",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
    lineHeight: 1.6,
    "& h3": {
      marginTop: "20px",
      marginBottom: "10px",
      color: theme.palette.primary.main,
      fontSize: "1.15rem",
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
    },
    "& h4": {
      marginTop: "16px",
      marginBottom: "8px",
      fontSize: "1.05rem",
      fontWeight: 600,
    },
    "& p": {
      marginBottom: "12px",
      fontSize: "0.95rem",
    },
    "& ul": {
      paddingLeft: "20px",
      marginBottom: "16px",
      "& li": {
        marginBottom: "8px",
        fontSize: "0.95rem",
      },
    },
    "& hr": {
      border: 0,
      height: "1px",
      background: "rgba(0, 0, 0, 0.1)",
      margin: "24px 0",
    },
    "& code": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
      padding: "2px 6px",
      borderRadius: "4px",
      fontFamily: "monospace",
      fontSize: "0.9rem",
    },
    "& blockquote": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: "0 0 16px 0",
      padding: "8px 16px",
      backgroundColor: "rgba(0, 0, 0, 0.02)",
      fontStyle: "italic",
    },
  },
  dialogActions: {
    padding: "16px 24px",
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  emptyState: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  formPreviewContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: "8px",
    backgroundColor: theme.palette.action.hover,
  },
  previewMediaBox: {
    width: "100%",
    aspectRatio: "16/9",
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "black",
    marginTop: theme.spacing(1),
  },
}));

const getPreviewText = (content) => {
  if (!content) return "";
  const cleanText = content
    .replace(/^###\s+.*$/gm, "")
    .replace(/\*{1,2}/g, "")
    .replace(/`{1,3}.*?`{1,3}/g, "")
    .replace(/---/g, "")
    .replace(/\n+/g, " ")
    .trim();
  return cleanText.length > 120 ? cleanText.substring(0, 120) + "..." : cleanText;
};

const Helps = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin" || user?.isSuper;

  const [records, setRecords] = useState([]);
  const { list, save, update, remove } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedManual, setSelectedManual] = useState(null);

  // Form Modal States for ADMs
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({ id: null, title: "", video: "", description: "" });
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (record) => setSelectedVideo(record);
  const closeVideoModal = () => setSelectedVideo(null);
  const openTermsModal = () => setTermsModalOpen(true);
  const closeTermsModal = () => setTermsModalOpen(false);
  const openManualModal = (manual, routeKey) => setSelectedManual({ ...manual, routeKey });
  const closeManualModal = () => setSelectedManual(null);

  const handleOpenFormModal = (record = null) => {
    if (record) {
      setFormData({
        id: record.id,
        title: record.title || "",
        video: record.video || "",
        description: record.description || ""
      });
    } else {
      setFormData({ id: null, title: "", video: "", description: "" });
    }
    setIsFormOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormOpen(false);
    setFormData({ id: null, title: "", video: "", description: "" });
  };

  const handleSaveVideo = async () => {
    if (!formData.title.trim()) {
      toast.error("O título do vídeo é obrigatório!");
      return;
    }
    if (!formData.video.trim()) {
      toast.error("A URL do vídeo é obrigatória!");
      return;
    }

    setSubmitting(true);
    try {
      if (formData.id) {
        await update(formData);
        toast.success("Vídeo atualizado com sucesso!");
      } else {
        await save(formData);
        toast.success("Vídeo adicionado com sucesso!");
      }
      const updatedHelps = await list();
      setRecords(updatedHelps);
      handleCloseFormModal();
    } catch (err) {
      toast.error("Erro ao salvar o vídeo. Verifique se os dados estão corretos.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVideo = async () => {
    if (!deletingRecord) return;
    setSubmitting(true);
    try {
      await remove(deletingRecord.id);
      toast.success("Vídeo removido com sucesso!");
      const updatedHelps = await list();
      setRecords(updatedHelps);
    } catch (err) {
      toast.error("Erro ao remover o vídeo.");
    } finally {
      setSubmitting(false);
      setDeletingRecord(null);
    }
  };

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") {
      closeVideoModal();
      closeTermsModal();
      closeManualModal();
      handleCloseFormModal();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => {
      document.removeEventListener("keydown", handleModalClose);
    };
  }, [handleModalClose]);

  const manualsList = useMemo(() => {
    return Object.keys(manuals).map((routeKey) => ({
      routeKey,
      ...manuals[routeKey],
    }));
  }, []);

  const filteredManuals = useMemo(() => {
    if (!searchTerm.trim()) return manualsList;
    const term = searchTerm.toLowerCase();
    return manualsList.filter(
      (m) =>
        m.title.toLowerCase().includes(term) ||
        m.routeKey.toLowerCase().includes(term) ||
        m.content.toLowerCase().includes(term)
    );
  }, [manualsList, searchTerm]);

  const renderVideoModal = () => {
    if (!selectedVideo) return null;
    const parsed = parseVideoUrl(selectedVideo.video);

    return (
      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        className={classes.videoModal}
      >
        <div className={classes.videoModalContent}>
          <IconButton
            onClick={closeVideoModal}
            style={{ position: "absolute", top: 8, right: 8, color: "#fff", zIndex: 10, backgroundColor: "rgba(0,0,0,0.5)" }}
            size="small"
          >
            <X size={20} />
          </IconButton>
          {parsed.type === "direct" ? (
            <video
              src={parsed.embedUrl}
              controls
              autoPlay
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <iframe
              style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
              src={parsed.embedUrl}
              title="Video Player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          )}
        </div>
      </Modal>
    );
  };

  const renderManualModal = () => (
    <Dialog
      open={Boolean(selectedManual)}
      onClose={closeManualModal}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="manual-dialog-title"
    >
      <DialogTitle id="manual-dialog-title" className={classes.dialogTitle} disableTypography>
        <Typography component="h2" className={classes.dialogTitleText}>
          <BookOpen size={22} />
          Manual: {selectedManual ? selectedManual.title : ""}
        </Typography>
        <IconButton onClick={closeManualModal} style={{ color: "#ffffff" }} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {selectedManual && <Markdown>{selectedManual.content}</Markdown>}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={closeManualModal}
          color="primary"
          variant="contained"
          style={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
        >
          Entendi, fechar manual
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderVideoFormModal = () => {
    const parsedPreview = parseVideoUrl(formData.video);

    return (
      <Dialog
        open={isFormOpen}
        onClose={handleCloseFormModal}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography component="h2" className={classes.dialogTitleText}>
            <Video size={22} />
            {formData.id ? "Editar Vídeo de Ajuda" : "Adicionar Vídeo de Ajuda (URL)"}
          </Typography>
          <IconButton onClick={handleCloseFormModal} style={{ color: "#ffffff" }} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            label="Título do Vídeo"
            variant="outlined"
            margin="dense"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Ex: Tutorial de Configuração de Filas"
          />

          <TextField
            label="URL / Link do Vídeo (YouTube, Google Drive, Vimeo, MP4)"
            variant="outlined"
            margin="dense"
            fullWidth
            value={formData.video}
            onChange={(e) => setFormData({ ...formData, video: e.target.value })}
            placeholder="Ex: https://www.youtube.com/watch?v=... ou https://drive.google.com/file/d/.../view"
            helperText="Cole o link do vídeo. Suporta links do YouTube, Google Drive, Vimeo ou links diretos MP4."
          />

          <TextField
            label="Descrição"
            variant="outlined"
            margin="dense"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Breve resumo do conteúdo explicativo do vídeo..."
          />

          {formData.video.trim() && (
            <div className={classes.formPreviewContainer}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Pré-visualização do Player ({parsedPreview.type.toUpperCase()}):
              </Typography>
              <div className={classes.previewMediaBox}>
                {parsedPreview.type === "direct" ? (
                  <video
                    src={parsedPreview.embedUrl}
                    controls
                    style={{ width: "100%", height: "100%" }}
                  />
                ) : (
                  <iframe
                    src={parsedPreview.embedUrl}
                    title="Preview"
                    style={{ width: "100%", height: "100%", border: 0 }}
                    allowFullScreen
                  />
                )}
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseFormModal} color="default" disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveVideo}
            color="primary"
            variant="contained"
            disabled={submitting}
            style={{ fontWeight: 600, borderRadius: "8px" }}
          >
            {submitting ? "Salvando..." : "Salvar Vídeo"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderManualsTab = () => (
    <div className={classes.mainPaperContainer}>
      <div className={classes.searchContainer}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Buscar por nome da tela (ex: Kanban, Atendimentos) ou conteúdo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} color="#888" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <X size={16} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={classes.mainPaper}>
        {filteredManuals.length > 0 ? (
          filteredManuals.map((item) => {
            const IconComp = routeIcons[item.routeKey] || BookOpen;
            return (
              <Paper
                key={item.routeKey}
                className={classes.manualCard}
                onClick={() => openManualModal(item, item.routeKey)}
              >
                <div className={classes.cardHeader}>
                  <div className={classes.cardIconBox}>
                    <IconComp className={classes.cardIcon} size={24} />
                  </div>
                  <Chip
                    label={item.routeKey}
                    size="small"
                    className={classes.routeBadge}
                  />
                </div>

                <Typography className={classes.cardTitle}>
                  {item.title}
                </Typography>

                <Typography className={classes.cardDescription}>
                  {getPreviewText(item.content)}
                </Typography>

                <div className={classes.readMoreText}>
                  Ler manual completo →
                </div>
              </Paper>
            );
          })
        ) : (
          <div className={classes.emptyState}>
            <HelpCircle size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Nenhum manual encontrado
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Não encontramos manuais correspondentes a "<strong>{searchTerm}</strong>". Tente buscar por outros termos.
            </Typography>
          </div>
        )}
      </div>
    </div>
  );

  const renderVideosTab = () => (
    <div className={classes.mainPaperContainer}>
      <div className={classes.mainPaper}>
        {/* Card Termos de Uso comentado conforme solicitação
        <Paper
          className={`${classes.helpPaper} ${classes.paperHover}`}
          onClick={openTermsModal}
        >
          <div className={classes.termsThumbnail}>
            <FileText size={96} />
          </div>
          <Typography variant="button" className={classes.videoTitle}>
            Termos de Uso
          </Typography>
          <Typography variant="caption" className={classes.videoDescription}>
            Clique aqui para visualizar os Termos e Condições de Uso do Mibia CRM.
          </Typography>
        </Paper>
        */}

        {records.length > 0
          ? records.map((record) => {
              const parsed = parseVideoUrl(record.video);

              return (
                <Paper
                  key={record.id}
                  className={`${classes.helpPaper} ${classes.paperHover}`}
                  onClick={() => openVideoModal(record)}
                >
                  {isAdmin && (
                    <div className={classes.cardActionOverlay}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFormModal(record);
                        }}
                        style={{ backgroundColor: "rgba(255, 255, 255, 0.9)", color: "#1976d2", padding: 6 }}
                      >
                        <Edit size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingRecord(record);
                        }}
                        style={{ backgroundColor: "rgba(255, 255, 255, 0.9)", color: "#d32f2f", padding: 6 }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </div>
                  )}

                  <div className={classes.mediaBox}>
                    {parsed.thumbnail ? (
                      <img
                        src={parsed.thumbnail}
                        alt={record.title}
                        className={classes.videoThumbnail}
                      />
                    ) : (
                      <div className={classes.genericThumbnail}>
                        {parsed.type === "gdrive" ? (
                          <HardDrive size={48} color="#4285F4" />
                        ) : (
                          <Play size={48} color="#fff" />
                        )}
                        <Typography variant="caption" style={{ fontWeight: 600, opacity: 0.9 }}>
                          {parsed.type === "gdrive" ? "Google Drive" : parsed.type === "vimeo" ? "Vimeo" : "Assistir Vídeo"}
                        </Typography>
                      </div>
                    )}
                  </div>

                  <Typography variant="button" className={classes.videoTitle}>
                    {record.title}
                  </Typography>
                  <Typography variant="caption" className={classes.videoDescription}>
                    {record.description}
                  </Typography>
                </Paper>
              );
            })
          : null}
      </div>
    </div>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("helps.title")}</Title>
        <MainHeaderButtonsWrapper></MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.headerContainer}>
        <div className={classes.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              label={
                <span className={classes.tabLabel}>
                  <BookOpen size={18} />
                  Manuais das Telas ({manualsList.length})
                </span>
              }
            />
            <Tab
              label={
                <span className={classes.tabLabel}>
                  <Video size={18} />
                  Vídeos e Tutoriais ({records.length})
                </span>
              }
            />
          </Tabs>

          {tabValue === 1 && isAdmin && (
            <Button
              variant="contained"
              color="primary"
              className={classes.addVideoButton}
              onClick={() => handleOpenFormModal()}
              startIcon={<Plus size={18} />}
            >
              Adicionar Vídeo
            </Button>
          )}
        </div>

        {tabValue === 0 && renderManualsTab()}
        {tabValue === 1 && renderVideosTab()}
      </div>

      {renderVideoModal()}
      {renderManualModal()}
      {renderVideoFormModal()}

      <ConfirmationModal
        title="Confirmar exclusão de vídeo"
        open={Boolean(deletingRecord)}
        onClose={() => setDeletingRecord(null)}
        onConfirm={handleDeleteVideo}
      >
        Tem certeza que deseja excluir o vídeo "{deletingRecord?.title}"? Esta ação não poderá ser desfeita.
      </ConfirmationModal>

      {/* <TermsOfUseModal open={termsModalOpen} onClose={closeTermsModal} /> */}
    </MainContainer>
  );
};

export default Helps;
